import type { ClinicalReference } from '~/types/clinical-reference';

// Workflow stages
export enum WorkflowStage {
  TRIAGE = 'TRIAGE',
  ELIGIBILITY = 'ELIGIBILITY',
  ACCEPTANCE = 'ACCEPTANCE',
  TRANSITION = 'TRANSITION',
  DISQUALIFIED = 'DISQUALIFIED'
}

// Patient location types
export enum LocationType {
  ED = 'ED',
  INPATIENT = 'INPATIENT'
}

// Change indicator types
export enum ChangeType {
  NEW = 'NEW',
  UPGRADED = 'UPGRADED',
  CLINICAL_CHANGE = 'CLINICAL_CHANGE',
  CLINICAL_DETERIORATION = 'CLINICAL_DETERIORATION',
  URGENT = 'URGENT'
}

// Disqualification reasons
export enum DisqualificationReason {
  CLINICALLY_UNSTABLE = 'CLINICALLY_UNSTABLE',
  NO_CAREGIVER = 'NO_CAREGIVER',
  OUT_OF_SERVICE_AREA = 'OUT_OF_SERVICE_AREA',
  PATIENT_DECLINED = 'PATIENT_DECLINED',
  INSURANCE_DENIAL = 'INSURANCE_DENIAL',
  ACUITY_TOO_HIGH = 'ACUITY_TOO_HIGH'
}

// Capacity check outcome (Capacity tab on the patient detail page).
// AVAILABLE -> success, LIMITED -> warning, UNAVAILABLE -> do not disturb.
export enum CapacityStatus {
  AVAILABLE = 'AVAILABLE',
  LIMITED = 'LIMITED',
  UNAVAILABLE = 'UNAVAILABLE'
}

// A patient's predefined capacity calendar. This is per-patient (NOT per
// template) so results are consistent across templates: admit slots are the
// same regardless of template, and the same future day + time slot always
// resolves to the same availability. The visit list, time ranges, and day count
// are derived from the selected template at check time; availability for each
// requested slot is sampled deterministically from this calendar.
export interface CapacityCalendar {
  // Human-readable admit visit time slots available today (e.g. '8:00 AM – 9:00 AM').
  admitSlots: string[]
  // Availability distribution sampled per (day, time slot). A given slot is
  // looked up deterministically, so two templates requesting the same future
  // day + time get the same answer.
  statusPool: CapacityStatus[]
}

// One visit entry within a day of a capacity result.
export interface CapacityVisitEntry {
  visitTypeName: string
  timeRange: string
  status: CapacityStatus
}

// One subsequent day in a capacity result.
export interface CapacityDay {
  label: string
  visits: CapacityVisitEntry[]
}

// Discharge programs
export enum PredictProgram {
  HOME_HEALTH = 'HOME_HEALTH',
  HOSPITAL_AT_HOME = 'HOSPITAL_AT_HOME',
  SNF_AT_HOME = 'SNF_AT_HOME',
  HOSPICE = 'HOSPICE',
  INFUSION_AT_HOME = 'INFUSION_AT_HOME',
  PALLIATIVE = 'PALLIATIVE'
}

// Programs visible without Hospital at Home
export const VISIBLE_PROGRAMS: PredictProgram[] = [
  PredictProgram.HOME_HEALTH,
  PredictProgram.SNF_AT_HOME,
  PredictProgram.HOSPICE,
  PredictProgram.INFUSION_AT_HOME,
  PredictProgram.PALLIATIVE,
];

// Programs visible when HCaH+ mode is enabled
export const HAH_PLUS_PROGRAMS: PredictProgram[] = [
  PredictProgram.HOSPITAL_AT_HOME,
  PredictProgram.HOME_HEALTH,
  PredictProgram.SNF_AT_HOME,
  PredictProgram.HOSPICE,
  PredictProgram.INFUSION_AT_HOME,
  PredictProgram.PALLIATIVE,
];

export type DemoMode = 'no-hah' | 'hah-plus';

// ==================== TWO-LAYER ORGANIZATION ARCHITECTURE ====================

// Layer 1: User Organization (who the user works for)
export interface UserOrganization {
  id: string
  name: string
  type: 'DISCHARGE_MANAGEMENT' | 'HEALTH_SYSTEM' | 'CARE_NETWORK'
}

// Layer 2: Client Organization (hospitals/health systems user has access to)
export interface ClientOrganization {
  id: string
  name: string
  type: 'HOSPITAL' | 'HEALTH_SYSTEM' | 'CARE_NETWORK'
}

// User role within their organization
export enum UserRole {
  CARE_COORDINATOR = 'CARE_COORDINATOR',
  CASE_MANAGER = 'CASE_MANAGER',
  CLINICAL_LIAISON = 'CLINICAL_LIAISON',
  RN_CASE_MANAGER = 'RN_CASE_MANAGER',
  REGIONAL_MANAGER = 'REGIONAL_MANAGER',
  ADMINISTRATOR = 'ADMINISTRATOR'
}

// Complete user profile with org layers
export interface PredictUser {
  id: string
  name: string
  email: string
  role: UserRole
  userOrganization: UserOrganization           // Layer 1: Who they work for
  clientOrganizations: ClientOrganization[]    // Layer 2: Which clients they can access
}

// ==================== END TWO-LAYER ARCHITECTURE ====================

// Decision factor
export interface DecisionFactor {
  id: string
  label: string
  importance: number // 0-100
  value: string
  dataSource: string
  timestamp: string // ISO 8601 format
  explanation: string
  contribution: 'positive' | 'negative' // Whether this factor helps or hurts the score
}

// Eligibility checklist question types
export enum QuestionType {
  YES_NO = 'YES_NO',
  YES_NO_UNKNOWN = 'YES_NO_UNKNOWN',
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  SELECT = 'SELECT',
  MULTI_SELECT = 'MULTI_SELECT'
}

// Single eligibility checklist item
export interface EligibilityChecklistItem {
  id: string
  question: string
  questionType: QuestionType
  required: boolean
  answer?: string | string[] | null
  options?: string[] // For SELECT and MULTI_SELECT types
  validation?: {
    min?: number
    max?: number
    pattern?: string
  }
  helperText?: string
  dataSource?: string
  lastUpdated?: string // ISO 8601 format
  updatedBy?: string
  autoPopulated?: boolean // Indicates if the answer was automatically filled from patient data
  // Answer evaluation criteria
  evaluationCriteria?: {
    disqualifyingAnswers?: string[] // Answers that disqualify the patient
    acceptableAnswers?: string[] // Answers that are acceptable (not ideal but not disqualifying)
    idealAnswers?: string[] // Answers that are ideal/preferred
  }
}

// Checklist section grouping
export interface ChecklistSection {
  id: string
  title: string
  description?: string
  items: EligibilityChecklistItem[]
}

// Complete eligibility checklist
export interface EligibilityChecklist {
  program: PredictProgram
  sections: ChecklistSection[]
  completionPercentage: number // 0-100
  lastUpdated: string // ISO 8601 format
  updatedBy: string
}

// Workflow transition log entry
export interface WorkflowTransition {
  id: string
  fromStage: WorkflowStage | null
  toStage: WorkflowStage
  timestamp: string // ISO 8601 format
  userId: string
  userName: string
  note: string
  reason?: string
  selectedProgram?: PredictProgram // Program selected at time of transition
  programChanged?: boolean // True if program was changed during this transition
}

// Patient assignment
export interface PatientAssignment {
  patientId: string
  userId: string | null
  userName: string | null
  userInitials: string | null
  assignedAt: string | null // ISO 8601 format
}

// Discharge patient
// 90-Day Summary types
export interface ClinicalEvent {
  date: string // ISO 8601 format
  type: 'admission' | 'discharge' | 'er_visit' | 'procedure' | 'medication_change' | 'pt_eval' | 'other'
  description: string
  location?: string
  // Where the patient went on a `discharge` event (e.g. 'Home', 'Home + Home
  // Health', 'SNF'). Drives the "Discharged To" stat in the 90-day summary.
  destination?: string
}

export interface NinetyDaySummary {
  narrativeSummary: string // AI-generated clinical narrative; may embed `[[n]]` citation markers
  references?: ClinicalReference[] // Sources backing the `[[n]]` markers in narrativeSummary
  recentAdmissions: number
  erVisits: number
  totalInpatientDays: number
  currentLOS: number // Current length of stay in days
  events: ClinicalEvent[]
  trend: 'improving' | 'stable' | 'declining'
  trendDescription: string // Explanation of the trend
  keyFindings: string[]
  lastUpdated: string // ISO 8601 format - when the clinical data was last updated
}

export interface PredictPatient {
  id: string

  // Patient info
  firstName: string
  lastName: string
  mrn: string
  dateOfBirth: string // ISO 8601 format
  age: number
  gender: string
  address: string // Patient address (includes zip code for filtering)
  phone?: string // Demo: made-up contact phone number

  // Clinical info
  facility: string
  department: string
  locationType: LocationType
  attendingPhysician: string
  primaryDiagnosis: string
  insuranceType: string
  expectedLOS: number // Expected length of stay in days
  currentLOS: number // Current length of stay in days

  // Discharge recommendation - NEW SCORING MODEL
  programScores: Partial<Record<PredictProgram, number>> // Score per program (0-100) - only relevant programs
  programDecisionFactors: Partial<Record<PredictProgram, DecisionFactor[]>> // Factors per program
  recommendedProgram: PredictProgram // The program with highest score (AI recommendation)
  selectedProgram?: PredictProgram // The program selected by user to work through workflow (undefined until the user picks one)
  currentStage: WorkflowStage

  // Assignment
  assignment?: PatientAssignment

  // Change tracking
  changeType?: ChangeType
  lastUpdated: string // ISO 8601 format

  // Disqualification (if applicable)
  disqualificationReason?: DisqualificationReason
  disqualificationNote?: string

  // Workflow history
  workflowHistory: WorkflowTransition[]

  // Eligibility checklist
  eligibilityChecklist?: EligibilityChecklist

  // Acceptance stage approvals
  providerApproval?: {
    approved: boolean
    providerName?: string
    approvedAt?: string // ISO 8601 format
  }
  patientAcceptance?: {
    accepted: boolean
    acceptedAt?: string // ISO 8601 format
    acceptedBy?: string // Who documented the acceptance
  }

  // Discharge status (Transition stage completion)
  discharged?: {
    completed: boolean
    dischargedAt?: string // ISO 8601 format
    dischargedBy?: string // Who documented the discharge
  }

  // 90-Day clinical summary
  ninetyDaySummary?: NinetyDaySummary

  // Capacity tab (demo): the patient's predefined capacity calendar used to
  // drive capacity checks consistently across templates. Always set for demo
  // patients.
  capacityCalendar?: CapacityCalendar

  // Capacity tab (demo): result of the most recent capacity check, persisted so
  // re-visiting the tab shows the prior result until the user checks again. The
  // detail is computed once at check time from the profile + template.
  capacityCheck?: {
    templateId: number
    templateName: string
    checkedAt: string // ISO 8601 format
    overallStatus: CapacityStatus
    admitSlots: string[] // admit visit time slots for admitSlotsDay
    admitSlotsDay: 'today' | 'tomorrow' // late in the day we fall back to tomorrow's slots
    days: CapacityDay[] // subsequent days, each with visits derived from the template
  }

  // Transfer stage (demo): read-only "transfer order entered" status shown on the
  // Transfer phase of the patient detail page.
  transferOrder?: {
    entered: boolean
    physician?: string
    enteredAt?: string // ISO 8601 format
  }

  // Qualify stage (demo): read-only EHR-driven verification statuses shown on the
  // Qualify phase (alongside the capacity check). Each checks off automatically
  // when the source system reports it.
  qualifyChecks?: {
    clinicalStability?: { verified: boolean; verifiedAt?: string; verifiedBy?: string }
    socialStability?: { verified: boolean; verifiedAt?: string; verifiedBy?: string }
    consent?: { verified: boolean; verifiedAt?: string; verifiedBy?: string }
  }

  // Demo: set when a user opens the patient detail view, used to clear the NEW badge
  viewedAt?: string

  // Demo: set when the patient has been promoted into the real patient list from
  // the Intake Queue so we can stop showing them there.
  scheduledAt?: string
}

// Sort configuration
export type SortField = 'name' | 'facility' | 'age' | 'diagnosis' | 'recommendedProgram' | 'stage' | 'duration' | 'updated' | 'expectedLOS' | 'currentLOS' | 'none'
export type SortDirection = 'asc' | 'desc'

// Column configuration
export type ColumnId =
  | 'name'
  | 'recommendedProgram'
  | 'stage'
  | 'locationType'
  | 'facility'
  | 'department'
  | 'attendingPhysician'
  | 'diagnosis'
  | 'insuranceType'
  | 'address'
  | 'expectedLOS'
  | 'currentLOS'
  | 'assignedTo'
  | 'daysAssigned'
  | 'lastActivity'
  | 'nextSteps'
  | 'daysInStage'
  | 'changeType'
  | 'updated'

export interface ColumnDefinition {
  id: ColumnId
  label: string
  description: string
  width?: string
  sortable: boolean
  defaultVisible: boolean
  wrappable?: boolean // If true, column text can wrap; if false, must not truncate
}

export interface ColumnConfig {
  visibleColumns: ColumnId[]
  columnOrder: ColumnId[]
  columnWidths: Record<string, string> // Maps columnId to custom width (e.g., '100px', '15%')
}

export const DEFAULT_COLUMNS: ColumnDefinition[] = [
  { id: 'name', label: 'Patient', description: 'Patient name, age, and MRN', width: '161px', sortable: true, defaultVisible: true, wrappable: false },
  { id: 'recommendedProgram', label: 'Score', description: 'Top recommended program with fit score (hover for all programs)', width: '157px', sortable: true, defaultVisible: true, wrappable: false },
  { id: 'locationType', label: 'Type', description: 'Location type (Inpatient/Emergency)', width: '70px', sortable: false, defaultVisible: true, wrappable: false },
  { id: 'facility', label: 'Location', description: 'Hospital facility, floor, and room', width: '160px', sortable: true, defaultVisible: true, wrappable: true },
  { id: 'department', label: 'Department', description: 'Hospital department', width: '82px', sortable: true, defaultVisible: false, wrappable: true },
  { id: 'attendingPhysician', label: 'Physician', description: 'Attending physician', width: '140px', sortable: true, defaultVisible: true, wrappable: false },
  { id: 'diagnosis', label: 'Primary Diagnosis', description: 'Primary diagnosis', width: '180px', sortable: true, defaultVisible: true, wrappable: true },
  { id: 'insuranceType', label: 'Insurance', description: 'Insurance type', sortable: true, defaultVisible: false, wrappable: true },
  { id: 'address', label: 'Address', description: 'Patient address', width: '200px', sortable: true, defaultVisible: false, wrappable: true },
  { id: 'expectedLOS', label: 'Expected Discharge', description: 'Expected discharge date', width: '140px', sortable: true, defaultVisible: true, wrappable: false },
  { id: 'currentLOS', label: 'Current LOS', description: 'Current length of stay (days)', width: '115px', sortable: true, defaultVisible: true, wrappable: false },
  { id: 'assignedTo', label: 'Assigned To', description: 'Assigned user', sortable: true, defaultVisible: false, wrappable: true },
  { id: 'daysAssigned', label: 'Days Assigned', description: 'Days since assignment', width: '120px', sortable: true, defaultVisible: false, wrappable: true },
  { id: 'lastActivity', label: 'Last Activity', description: 'Most recent action', width: '120px', sortable: true, defaultVisible: false, wrappable: true },
  { id: 'nextSteps', label: 'Next Steps', description: 'Recommended next actions', sortable: false, defaultVisible: false, wrappable: true },
  { id: 'daysInStage', label: 'Days in Stage', description: 'Days in current stage', width: '120px', sortable: true, defaultVisible: false, wrappable: true },
  { id: 'changeType', label: 'Change', description: 'Change indicator', width: '100px', sortable: true, defaultVisible: false, wrappable: true },
  { id: 'updated', label: 'Updated', description: 'Last updated time', width: '120px', sortable: true, defaultVisible: false, wrappable: true },
]

export const DEFAULT_COLUMN_CONFIG: ColumnConfig = {
  visibleColumns: DEFAULT_COLUMNS.filter(col => col.defaultVisible).map(col => col.id),
  columnOrder: DEFAULT_COLUMNS.map(col => col.id),
  columnWidths: {}, // Custom widths will be stored here
}

// Filter state (local to view, not in context)
export interface PredictFilters {
  locationType: LocationType | 'ALL'
  facility: string | 'ALL'
  department: string | 'ALL'
  workflowStage: WorkflowStage[] // Multi-select array of stages
  program: PredictProgram | 'ALL'
  insuranceType: string | 'ALL'
  zipCode: string | 'ALL'
  ageRange: string | 'ALL' // e.g. '18-29', '30-39', '40-49', etc.
  ownershipFilter: 'MY' | 'UNASSIGNED' | 'ALL'
  changeTypes: ChangeType[] // Multi-select array of change indicators
  searchQuery: string
  sortBy: SortField
  sortDirection: SortDirection
}

// Individual requirement within a stage
export interface ReadinessRequirement {
  id: string
  label: string
  description: string
  isMet: boolean
  value?: string | number // Current value
  targetValue?: string | number // Required value
  category: 'critical' | 'required' | 'optional'
  subRequirements?: ReadinessRequirement[] // Nested requirements (e.g., problematic questions)
}

// Overall readiness status for current stage
export interface StageReadinessDetails {
  stage: WorkflowStage
  isReady: boolean
  overallPercentage: number // 0-100
  requirements: ReadinessRequirement[]
  nextStageLabel: string
  blockerMessage?: string
}

// Context type
export interface PredictContextType {
  patients: PredictPatient[]
  loading: boolean
  currentUser: PredictUser | null
  getPatientById: (id: string) => PredictPatient | undefined
  takeOwnership: (patientId: string) => void
  releaseOwnership: (patientId: string) => void
  assignOwnership: (patientId: string, userId: string) => void
  transitionWorkflow: (patientId: string, toStage: WorkflowStage, note: string, reason?: string) => void
  updatePatient: (patientId: string, updates: Partial<PredictPatient>) => void
  updateChecklistAnswer: (patientId: string, itemId: string, answer: string | string[] | null) => void
  initializeChecklist: (patientId: string) => void
  switchDemoUser: (user: PredictUser) => void
  triggerDemoScenario: (scenarioId: string) => void
  resetDemoScenario: (scenarioId: string) => void
  demoMode: DemoMode
  setDemoMode: (mode: DemoMode) => void
  visiblePrograms: PredictProgram[]
  selectProgram: (patientId: string, program: PredictProgram) => void
  changeProgram: (patientId: string, newProgram: PredictProgram) => void
  isReadyToAdvance: (patientId: string) => boolean
  getReadinessDetails: (patientId: string) => StageReadinessDetails
  setProviderApproval: (patientId: string, approved: boolean, providerName?: string) => void
  setPatientAcceptance: (patientId: string, accepted: boolean) => void
  setPatientDischarged: (patientId: string, discharged: boolean) => void
  markPatientViewed: (patientId: string) => void
  markPatientScheduled: (patientId: string) => void
}

// Eligibility checklist templates for each program
export const PROGRAM_ELIGIBILITY_CHECKLISTS: Record<PredictProgram, Omit<EligibilityChecklist, 'completionPercentage' | 'lastUpdated' | 'updatedBy'>> = {
  [PredictProgram.HOME_HEALTH]: {
    program: PredictProgram.HOME_HEALTH,
    sections: [
      {
        id: 'hh-clinical',
        title: 'Clinical Requirements',
        description: 'Patient clinical status and care needs',
        items: [
          {
            id: 'hh-homebound',
            question: 'Is the patient homebound?',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true,
            helperText: 'Patient must be homebound (confined to home) to qualify for home health',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              acceptableAnswers: ['Unknown'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hh-skilled-need',
            question: 'Does the patient require skilled nursing or therapy?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'At least one skilled service (nursing, PT, OT, ST) must be needed',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hh-skilled-services',
            question: 'Which skilled services are needed?',
            questionType: QuestionType.MULTI_SELECT,
            required: true,
            options: ['Skilled Nursing', 'Physical Therapy', 'Occupational Therapy', 'Speech Therapy']
          },
          {
            id: 'hh-stable',
            question: 'Is the patient medically stable?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Patient should be stable enough for home-based care',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          }
        ]
      },
      {
        id: 'hh-social',
        title: 'Social & Environmental',
        description: 'Home environment and caregiver support',
        items: [
          {
            id: 'hh-caregiver',
            question: 'Is a caregiver available?',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true,
            helperText: 'Caregiver may be required depending on patient needs'
          },
          {
            id: 'hh-caregiver-availability',
            question: 'Caregiver availability',
            questionType: QuestionType.SELECT,
            required: false,
            options: ['24/7', 'Daytime only', 'Intermittent', 'None']
          },
          {
            id: 'hh-home-safe',
            question: 'Is the home environment safe?',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true,
            helperText: 'Home should be safe for patient and care team',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              acceptableAnswers: ['Unknown'],
              idealAnswers: ['Yes']
            }
          }
        ]
      },
      {
        id: 'hh-insurance',
        title: 'Insurance & Authorization',
        description: 'Coverage and authorization requirements',
        items: [
          {
            id: 'hh-insurance-authorization',
            question: 'Insurance authorization received',
            questionType: QuestionType.YES_NO,
            required: true
          },
          {
            id: 'hh-physician-order',
            question: 'Has physician signed home care orders?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          }
        ]
      }
    ]
  },
  [PredictProgram.HOSPITAL_AT_HOME]: {
    program: PredictProgram.HOSPITAL_AT_HOME,
    sections: [
      {
        id: 'hah-clinical',
        title: 'Clinical Screening',
        description: 'Clinical criteria derived from chart review. Confirms the patient\'s condition is acute enough to warrant hospital-level care but does not require services only available in a brick-and-mortar facility.',
        items: [
          {
            id: 'hah-diagnosis',
            question: 'Admitting diagnosis is appropriate for Hospital at Home acute care',
            questionType: QuestionType.SELECT,
            required: true,
            options: [
              'COPD exacerbation (moderate)',
              'Community-acquired pneumonia (low-to-moderate severity)',
              'Congestive heart failure exacerbation',
              'Cellulitis / skin and soft tissue infection requiring IV antibiotics',
              'Urinary tract infection / pyelonephritis',
              'DVT / pulmonary embolism (low-risk, anticoagulation initiation)',
              'Dehydration / electrolyte imbalance',
              'Other acute medical condition'
            ],
            helperText: 'Hospital at Home is appropriate for acute conditions that require hospital-level oversight but not ICU-level intervention or same-day procedural care',
            evaluationCriteria: {
              idealAnswers: [
                'COPD exacerbation (moderate)',
                'Community-acquired pneumonia (low-to-moderate severity)',
                'Congestive heart failure exacerbation',
                'Cellulitis / skin and soft tissue infection requiring IV antibiotics',
                'Urinary tract infection / pyelonephritis',
                'DVT / pulmonary embolism (low-risk, anticoagulation initiation)',
                'Dehydration / electrolyte imbalance'
              ]
            }
          },
          {
            id: 'hah-hemodynamic-stability',
            question: 'Patient is hemodynamically stable (BP, HR, RR within acceptable range without vasopressors)',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Hemodynamic instability requiring vasopressors or continuous IV titration is a contraindication for Hospital at Home',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-oxygen',
            question: 'Supplemental oxygen requirement is ≤2L nasal cannula at rest with SpO2 ≥92%',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true,
            helperText: 'Higher O2 requirements or need for high-flow oxygen, BiPAP, or ventilator support are contraindications',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              acceptableAnswers: ['Unknown'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-no-icu',
            question: 'No active indication for ICU-level monitoring or intervention',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Includes continuous cardiac monitoring, arterial lines, frequent neuro checks, or other ICU-specific protocols',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-no-procedure',
            question: 'No pending surgical procedure or intervention requiring the OR, IR, or endoscopy suite within 24 hours',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-cognition',
            question: 'Patient is alert and oriented, able to cooperate with care team and follow instructions',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Acute altered mental status, severe dementia, or active psychiatric crisis may preclude safe participation in Hospital at Home',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-iv-therapy',
            question: 'If IV therapy is required, home infusion can be arranged through Hospital at Home program',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: false,
            helperText: 'Hospital at Home programs can administer IV antibiotics, IV diuretics, and other IV medications at home. Note if home infusion pharmacy is available.',
            evaluationCriteria: {
              acceptableAnswers: ['Unknown'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-episode-length',
            question: 'Anticipated Hospital at Home episode is ≤7 days',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true,
            helperText: 'Hospital at Home acute episodes typically range 3–7 days. Conditions requiring longer acute oversight may not be appropriate.',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              acceptableAnswers: ['Unknown'],
              idealAnswers: ['Yes']
            }
          }
        ]
      },
      {
        id: 'hah-social',
        title: 'Social & Environmental Screening',
        description: 'Requires in-person or phone assessment by the Hospital at Home care team or social worker. Must be completed before enrollment.',
        items: [
          {
            id: 'hah-caregiver-available',
            question: 'A capable adult caregiver is available and present in the home during the Hospital at Home episode',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Hospital at Home requires a responsible adult present during daily physician rounds and available to respond to clinical changes between visits',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-caregiver-willing',
            question: 'Caregiver is willing and able to participate in care (medication administration, monitoring support, emergency response)',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-patient-consent',
            question: 'Patient and caregiver have been educated on Hospital at Home and consent to participation',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-home-access',
            question: 'Care team can access the home (ground floor or elevator access; no barriers to equipment delivery)',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Hospital at Home staff and medical equipment (IV poles, RPM devices, oxygen) must be able to enter and move within the home',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-home-environment',
            question: 'Home environment supports safe care delivery (adequate space, clean, appropriate temperature control, functioning utilities)',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Assessed during home visit or structured phone screening. Includes space for care team, working heat/AC, and no conditions posing immediate safety risk to patient or staff.',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-connectivity',
            question: 'Reliable WiFi or cellular signal available at home for remote monitoring and telehealth visits',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Required for RPM device transmission and daily virtual physician rounds. Cellular-enabled backup RPM devices can substitute for WiFi in some programs.',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-proximity',
            question: 'Patient\'s home is within the Hospital at Home program\'s geographic service area (typically ≤30 min drive from program hub)',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true,
            helperText: 'Geographic eligibility is required for timely in-person clinical response. Confirm address against program service map.',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              acceptableAnswers: ['Unknown'],
              idealAnswers: ['Yes']
            }
          }
        ]
      },
      {
        id: 'hah-insurance',
        title: 'Insurance & Authorization',
        description: 'Payer coverage and pre-authorization. Hospital at Home is typically covered as an inpatient-equivalent benefit under Medicare Advantage, select commercial plans, and Medicaid waiver programs.',
        items: [
          {
            id: 'hah-insurance-coverage',
            question: 'Patient\'s insurance plan covers Hospital at Home as an acute inpatient-equivalent benefit',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true,
            helperText: 'Medicare Advantage plans and select commercial payers cover Hospital at Home. Traditional Medicare covers Hospital at Home under the Acute Hospital Care at Home waiver program.',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              acceptableAnswers: ['Unknown'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hah-authorization',
            question: 'Prior authorization has been obtained or payer has confirmed coverage without prior auth requirement',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          }
        ]
      }
    ]
  },
  [PredictProgram.SNF_AT_HOME]: {
    program: PredictProgram.SNF_AT_HOME,
    sections: [
      {
        id: 'snf-eligibility',
        title: 'Eligibility Requirements',
        description: 'Core requirements for SNF at Home eligibility',
        items: [
          {
            id: 'snf-qualifying-insurance',
            question: 'Does patient have qualifying insurance (contracted payer for SNF at Home)?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Patient must have insurance contracted with SNF at Home program',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          },
          {
            id: 'snf-geographic-area',
            question: 'Does patient live within available geographic area for home health?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Patient address must be within service area',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          },
          {
            id: 'snf-certified-need',
            question: 'Does patient have certified need for home health skilled nursing and/or therapy?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Physician must certify need for skilled services',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          },
          {
            id: 'snf-bundle-services',
            question: 'Can patient clinical and ancillary needs be met through SNF at Home bundle of services?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'All patient needs must be manageable within SNF at Home service bundle',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          },
          {
            id: 'snf-willing-participate',
            question: 'Is patient willing and able to participate in plan of care?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Patient must consent and be able to engage in care plan',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          }
        ]
      },
      {
        id: 'snf-exclusion',
        title: 'Exclusion Criteria',
        description: 'Criteria that may exclude patients from SNF at Home',
        items: [
          {
            id: 'snf-age-requirement',
            question: 'Is patient 18 years or older?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Patient must be 18 years or older for SNF at Home',
            dataSource: 'Auto-populated from patient age',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          },
          {
            id: 'snf-los-requirement',
            question: 'Is expected length of stay 30 days or less?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'SNF at Home is designed for shorter-term care (≤30 days)',
            dataSource: 'Auto-populated from expected LOS',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          },
          {
            id: 'snf-caregiver-support',
            question: 'Does patient have appropriate level of caregiver support?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Adequate caregiver support is essential for SNF at Home success',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          },
          {
            id: 'snf-home-environment',
            question: 'Does patient have suitable/safe home environment to promote recovery?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Home must be safe and conducive to recovery',
            evaluationCriteria: {
              disqualifyingAnswers: ['NO'],
              idealAnswers: ['YES']
            }
          }
        ]
      },
      {
        id: 'snf-clinical',
        title: 'Clinical Assessment',
        description: 'Additional clinical status and care needs',
        items: [
          {
            id: 'snf-rehab-potential',
            question: 'Does patient have rehabilitation potential?',
            questionType: QuestionType.YES_NO,
            required: false,
            helperText: 'SNF at Home focuses on rehabilitation and recovery'
          },
          {
            id: 'snf-therapy-hours',
            question: 'Can patient tolerate therapy hours required?',
            questionType: QuestionType.YES_NO,
            required: false,
            helperText: 'Typically requires 3+ hours of therapy per day'
          },
          {
            id: 'snf-therapy-types',
            question: 'Which therapies are needed?',
            questionType: QuestionType.MULTI_SELECT,
            required: false,
            options: ['Physical Therapy', 'Occupational Therapy', 'Speech Therapy']
          },
          {
            id: 'snf-medical-stable',
            question: 'Is patient medically stable?',
            questionType: QuestionType.YES_NO,
            required: false
          },
          {
            id: 'snf-adl-assist',
            question: 'Level of ADL assistance needed',
            questionType: QuestionType.SELECT,
            required: false,
            options: ['Independent', 'Minimal assist', 'Moderate assist', 'Maximum assist', 'Total dependence']
          }
        ]
      },
      {
        id: 'snf-social',
        title: 'Social & Environmental',
        description: 'Home environment details',
        items: [
          {
            id: 'snf-home-accessible',
            question: 'Is home wheelchair accessible if needed?',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: false
          },
          {
            id: 'snf-equipment',
            question: 'Can home accommodate necessary equipment?',
            questionType: QuestionType.YES_NO,
            required: false,
            helperText: 'May include hospital bed, walker, wheelchair, etc.'
          }
        ]
      }
    ]
  },
  [PredictProgram.HOSPICE]: {
    program: PredictProgram.HOSPICE,
    sections: [
      {
        id: 'hospice-clinical',
        title: 'Clinical Requirements',
        description: 'Patient clinical status and prognosis',
        items: [
          {
            id: 'hospice-prognosis',
            question: 'Physician certifies life expectancy ≤ 6 months?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Physician must certify terminal prognosis',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hospice-curative-declined',
            question: 'Has patient declined curative treatment?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Patient must elect hospice care over curative treatment',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hospice-comfort-focus',
            question: 'Is focus on comfort care?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hospice-symptoms',
            question: 'Primary symptoms requiring management',
            questionType: QuestionType.MULTI_SELECT,
            required: false,
            options: ['Pain', 'Dyspnea', 'Nausea/Vomiting', 'Anxiety', 'Depression', 'Delirium', 'Other']
          }
        ]
      },
      {
        id: 'hospice-social',
        title: 'Social & Emotional',
        description: 'Patient and family support needs',
        items: [
          {
            id: 'hospice-patient-aware',
            question: 'Is patient aware of prognosis?',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true
          },
          {
            id: 'hospice-family-support',
            question: 'Does patient have family/caregiver support?',
            questionType: QuestionType.YES_NO,
            required: true
          },
          {
            id: 'hospice-preferences',
            question: 'Has patient expressed care preferences?',
            questionType: QuestionType.YES_NO,
            required: false,
            helperText: 'Including location of care, DNR status, etc.'
          }
        ]
      },
      {
        id: 'hospice-insurance',
        title: 'Insurance & Authorization',
        description: 'Coverage and authorization requirements',
        items: [
          {
            id: 'hospice-medicare-benefit',
            question: 'Does patient have hospice benefit available?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'hospice-election',
            question: 'Has patient signed hospice election form?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          }
        ]
      }
    ]
  },
  [PredictProgram.INFUSION_AT_HOME]: {
    program: PredictProgram.INFUSION_AT_HOME,
    sections: [
      {
        id: 'infusion-clinical',
        title: 'Clinical Requirements',
        description: 'Patient clinical status and infusion needs',
        items: [
          {
            id: 'infusion-type',
            question: 'Type of infusion therapy required',
            questionType: QuestionType.SELECT,
            required: true,
            options: ['Antibiotics', 'Chemotherapy', 'Immunoglobulin', 'Hydration', 'Pain management', 'TPN', 'Other']
          },
          {
            id: 'infusion-frequency',
            question: 'Infusion frequency',
            questionType: QuestionType.SELECT,
            required: true,
            options: ['Multiple times daily', 'Daily', 'Every other day', 'Weekly', 'As needed']
          },
          {
            id: 'infusion-duration',
            question: 'Expected duration of therapy (days)',
            questionType: QuestionType.NUMBER,
            required: true,
            validation: { min: 1, max: 365 }
          },
          {
            id: 'infusion-vascular-access',
            question: 'Type of vascular access',
            questionType: QuestionType.SELECT,
            required: true,
            options: ['Peripheral IV', 'PICC line', 'Mid-line', 'Port', 'Other']
          },
          {
            id: 'infusion-stable',
            question: 'Is patient medically stable for home infusion?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          }
        ]
      },
      {
        id: 'infusion-social',
        title: 'Social & Environmental',
        description: 'Home environment and support',
        items: [
          {
            id: 'infusion-caregiver',
            question: 'Is a caregiver available during infusions?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Caregiver may be required depending on infusion type'
          },
          {
            id: 'infusion-refrigeration',
            question: 'Is refrigeration available if needed?',
            questionType: QuestionType.YES_NO,
            required: false,
            helperText: 'Some medications require refrigeration'
          },
          {
            id: 'infusion-storage',
            question: 'Is there adequate storage space for supplies?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          }
        ]
      },
      {
        id: 'infusion-insurance',
        title: 'Insurance & Authorization',
        description: 'Coverage and authorization requirements',
        items: [
          {
            id: 'infusion-coverage',
            question: 'Does insurance cover home infusion?',
            questionType: QuestionType.YES_NO_UNKNOWN,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              acceptableAnswers: ['Unknown'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'infusion-authorization',
            question: 'Has prior authorization been obtained?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'infusion-pharmacy',
            question: 'Has home infusion pharmacy been identified?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          }
        ]
      }
    ]
  },
  [PredictProgram.PALLIATIVE]: {
    program: PredictProgram.PALLIATIVE,
    sections: [
      {
        id: 'palliative-clinical',
        title: 'Clinical Requirements',
        description: 'Patient clinical status and needs',
        items: [
          {
            id: 'palliative-serious-illness',
            question: 'Does patient have serious illness?',
            questionType: QuestionType.YES_NO,
            required: true,
            helperText: 'Palliative care addresses serious illness at any stage',
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          },
          {
            id: 'palliative-symptom-burden',
            question: 'Does patient have significant symptom burden?',
            questionType: QuestionType.YES_NO,
            required: true
          },
          {
            id: 'palliative-concurrent',
            question: 'Will patient continue curative treatment?',
            questionType: QuestionType.YES_NO,
            required: false,
            helperText: 'Palliative care can be provided alongside curative treatment'
          }
        ]
      },
      {
        id: 'palliative-goals',
        title: 'Goals of Care',
        description: 'Patient goals and preferences',
        items: [
          {
            id: 'palliative-goals-discussion',
            question: 'Have goals of care been discussed?',
            questionType: QuestionType.YES_NO,
            required: true
          },
          {
            id: 'palliative-quality-focus',
            question: 'Is improving quality of life a priority?',
            questionType: QuestionType.YES_NO,
            required: true
          }
        ]
      },
      {
        id: 'palliative-insurance',
        title: 'Insurance & Authorization',
        description: 'Coverage and authorization requirements',
        items: [
          {
            id: 'palliative-insurance-authorization',
            question: 'Insurance authorization received',
            questionType: QuestionType.YES_NO,
            required: true
          },
          {
            id: 'palliative-physician-order',
            question: 'Has physician signed home care orders?',
            questionType: QuestionType.YES_NO,
            required: true,
            evaluationCriteria: {
              disqualifyingAnswers: ['No'],
              idealAnswers: ['Yes']
            }
          }
        ]
      }
    ]
  }
}
